import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { CalendarClock, Lock, Trash2 } from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../hooks/useUsage";
import { canScheduleReports } from "../utils/jwtUtils";
import {
  FREQUENCY_LABELS,
  SCHEDULE_REPORT_TYPES,
  deleteSchedule,
  listSchedules,
} from "../utils/scheduleApi";
import LoadingSpinner from "./LoadingSpinner";
import ScheduleReportModal from "./ScheduleReportModal";
import UsageMeter from "./UsageMeter";

const scheduleId = (s) => s.id ?? s._id;

const ScheduledReports = () => {
  const { user } = useAuth();
  const { byFeature, refresh: refreshUsage } = useUsage();

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(SCHEDULE_REPORT_TYPES[0].value);
  const [modalLockType, setModalLockType] = useState(false);

  const allowed = canScheduleReports(user?.subscriptionPlan);
  const accountEmail = user?.email ?? "";

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listSchedules(undefined, signal ? { signal } : undefined);
      const body = res.data;
      setSchedules(Array.isArray(body) ? body : (body?.data ?? []));
    } catch (err) {
      if (signal?.aborted) return;
      setError("Couldn't load your schedules.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [allowed, load]);

  const byType = useMemo(() => {
    const map = {};
    for (const s of schedules) {
      (map[s.report_type] ??= []).push(s);
    }
    return map;
  }, [schedules]);

  const openNewSchedule = (type, lockType = false) => {
    setModalType(type);
    setModalLockType(lockType);
    setIsModalOpen(true);
  };

  const handleModalChange = (open) => {
    setIsModalOpen(open);
    if (!open) {
      load();
      refreshUsage();
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteSchedule(id);
      setSchedules((prev) => prev.filter((s) => scheduleId(s) !== id));
    } catch {
      setError("Failed to cancel schedule.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen pt-10 pb-16 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-headline text-2xl font-bold text-on-surface">Scheduled Reports</h1>
            <p className="text-sm text-on-surface-variant mt-1">
              Reports run automatically on your chosen cadence and are emailed to you.
            </p>
          </div>
          {allowed && (
            <button
              onClick={() => openNewSchedule(SCHEDULE_REPORT_TYPES[0].value)}
              className="shrink-0 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-primary to-primary-container text-on-primary rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <CalendarClock className="w-4 h-4" />
              New schedule
            </button>
          )}
        </div>

        {!allowed ? (
          <div className="py-14 text-center border border-dashed border-surface-container-high rounded-2xl">
            <Lock className="w-10 h-10 text-outline mx-auto mb-3" />
            <p className="text-on-surface font-medium">Report scheduling is a paid feature.</p>
            <p className="text-on-surface-variant text-sm mt-1">
              Upgrade to the Optimized plan or higher to schedule recurring reports.
            </p>
            <Link
              to="/pricing"
              className="inline-block mt-4 text-primary text-sm font-semibold hover:underline"
            >
              View plans
            </Link>
          </div>
        ) : loading ? (
          <div className="py-16 flex justify-center">
            <LoadingSpinner size="md" />
          </div>
        ) : error ? (
          <p className="text-error text-sm">{error}</p>
        ) : (
          <div className="space-y-4">
            {SCHEDULE_REPORT_TYPES.map((type) => {
              const items = byType[type.value] ?? [];
              const usageItem = type.usageFeature ? byFeature?.[type.usageFeature] : null;
              return (
                <section
                  key={type.value}
                  className="bg-surface-container-low rounded-2xl border border-surface-container-high p-5"
                >
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div>
                      <h2 className="text-base font-bold text-on-surface">{type.label}</h2>
                      {usageItem && <UsageMeter item={usageItem} className="mt-1" />}
                    </div>
                    <button
                      onClick={() => openNewSchedule(type.value, true)}
                      className="shrink-0 text-primary text-sm font-medium hover:underline"
                    >
                      New schedule
                    </button>
                  </div>

                  {items.length === 0 ? (
                    <p className="text-on-surface-variant text-sm">No schedules yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {items.map((s) => {
                        const id = scheduleId(s);
                        const freqLabel = FREQUENCY_LABELS[s.frequency] ?? s.frequency;
                        return (
                          <li
                            key={id}
                            className="flex items-start justify-between gap-4 p-4 bg-surface-container-lowest rounded-xl border border-surface-container-high"
                          >
                            <div className="min-w-0">
                              <span className="text-xs font-bold uppercase tracking-wider text-primary">
                                {freqLabel}
                              </span>
                              {s.target && (
                                <p className="text-sm font-medium text-on-surface truncate mt-0.5">
                                  {s.target}
                                </p>
                              )}
                              <p className="text-xs text-on-surface-variant mt-1">
                                {s.recipients?.join(", ")}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDelete(id)}
                              disabled={deletingId === id}
                              title="Cancel schedule"
                              aria-label="Cancel schedule"
                              className="shrink-0 p-2 rounded-lg text-on-surface-variant hover:text-error hover:bg-surface-container-high transition-colors disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      {allowed && (
        <ScheduleReportModal
          open={isModalOpen}
          onOpenChange={handleModalChange}
          accountEmail={accountEmail}
          usageByFeature={byFeature}
          defaultType={modalType}
          lockType={modalLockType}
          onCreated={() => {
            load();
            refreshUsage();
          }}
        />
      )}
    </div>
  );
};

export default ScheduledReports;
